import { forwardRef } from 'react';
import { formatKoreanAmount } from '../../utils/quoteFormat';
import { type PublicQuizQuote } from '../utils/publicQuizQuote';
const ASSET_BASE = '/assets/pricing/quote/document';

/**
 * 견적서는 표 하나에 셀이 60개쯤 되고 전부 같은 테두리·정렬을 쓴다. 셀마다 클래스를
 * 붙이는 대신 표에 자손 variant 를 건다. `pdf` 는 인쇄용으로 여백·줄높이만 줄인 것.
 */
/**
 * 견적서는 표 하나에 셀이 60개쯤 되고 전부 같은 테두리·정렬을 쓴다. 셀마다 클래스를
 * 붙이는 대신 표에 자손 variant 를 건다. `pdf` 는 인쇄용으로 여백·줄높이만 줄인 것.
 *
 * 분기마다 클래스를 통째로 적는다 — Tailwind 는 소스에서 완성된 문자열만 찾으므로
 * 템플릿으로 조립하면 그 클래스가 생성되지 않는다.
 */
const CELL_BASE = '[&_td]:border-b [&_td]:border-r [&_td]:border-[#ccc] [&_td]:text-center [&_td]:align-middle ' + '[&_th]:border-b [&_th]:border-r [&_th]:border-[#ccc] [&_th]:text-center [&_th]:align-middle [&_th]:bg-[#f0f0f0] ' + '[&_td:first-child]:border-l [&_th:first-child]:border-l';
const CELL_SIZE_PREVIEW = '[&_td]:p-[5px] [&_td]:leading-[12px] [&_th]:p-[5px] [&_th]:leading-[12px]';
const CELL_SIZE_PDF = '[&_td]:p-[4px] [&_td]:leading-[11px] [&_th]:p-[4px] [&_th]:leading-[11px]';
function tableClass(pdf: boolean, extra = '') {
  return ['w-full border-separate border-spacing-0 border-y-2 border-y-[#000]', pdf ? 'mt-[8px]' : 'mt-[10px]', CELL_BASE, pdf ? CELL_SIZE_PDF : CELL_SIZE_PREVIEW, extra].filter(Boolean).join(' ');
}
const PLAN_LABEL_KO = {
  basic: '베이직',
  pro: '프로'
} as const;
const PLAN_LABEL_EN = {
  basic: 'Basic',
  pro: 'Pro'
} as const;
interface QuizQuoteDocumentProps {
  /** 발행 직후(IssuedQuizQuote)든 토큰으로 불러온 공개본이든 같은 문서를 그린다. */
  quote: PublicQuizQuote;
  variant?: 'preview' | 'pdf';
}
function formatWon(value: number): string {
  return value.toLocaleString('ko-KR');
}
function AmountCell({
  amount,
  compact = false,
  parenthesized = false
}: {
  amount: number;
  compact?: boolean;
  parenthesized?: boolean;
}) {
  // 괄호는 셀 폭에 영향을 주면 안 돼서 의사 요소로 바깥에 띄운다(할인 행의 "(금액)").
  const parentheses = parenthesized ? "before:absolute before:top-0 before:w-[6px] before:text-center before:content-['('] before:right-[calc(100%-13px)] after:absolute after:top-0 after:w-[6px] after:text-center after:content-[')'] after:left-[calc(100%-13px)]" : '';
  return <span className={`relative mx-auto block ${compact ? 'w-[68px] p-0' : 'w-[128px]'} ${parentheses}`} data-sentry-component="AmountCell" data-sentry-source-file="QuizQuoteDocument.tsx">
      <span className={`box-border grid grid-cols-[12px_1fr] items-center gap-x-px ${compact ? 'mx-auto w-[68px] p-0' : 'w-full px-[18px]'}`}>
        <span className="text-center">₩</span>
        <span className="text-right tabular-nums">{formatWon(amount)}</span>
      </span>
    </span>;
}
function formatDocumentDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
}
function getPlanFeature(plan: PublicQuizQuote['product']['plan']): string {
  if (plan === 'basic') {
    return '베이직 전용 맵 이용, AI로 문제 추가하기, 비공개 퀴즈 만들기';
  }
  return '모든 맵 무제한 이용, 고급 AI 기능, 모든 학습리포트 기능, 다음 퀴즈 이어풀기, 워크시트 저장';
}
export function getQuoteDiscountRows(discounts: PublicQuizQuote['pricing']['discounts']) {
  return discounts.map(({
    label,
    amount
  }) => ({
    label,
    amount
  }));
}
export const QuizQuoteDocument = forwardRef<HTMLDivElement, QuizQuoteDocumentProps>(function QuizQuoteDocument({
  quote,
  variant = 'preview'
}, ref) {
  const pdf = variant === 'pdf';
  const annotation = quote.recipient.annotation || '특이사항 없음';
  const discountRows = getQuoteDiscountRows(quote.pricing.discounts);
  const emptyRowCount = Math.max(0, 4 - discountRows.length - (pdf ? 1 : 0));
  return <div ref={ref} className={`relative border border-[#e9eaf2] bg-white font-sans shadow-[0_4px_8px_0_rgb(28_27_31_/_8%)] ${pdf ? 'px-[24px] pb-[16px] pt-[18px] text-[9.6px] leading-[11px]' : 'p-[24px] pb-[30px] text-[10px] leading-[12px]'}`}>
      <div className={`text-center font-bold ${pdf ? 'mb-[20px] mt-[12px] text-[14px] leading-[17px]' : 'mb-[30px] mt-[20px] text-[15px] leading-[18px]'}`}>
        ZEP QUIZ {PLAN_LABEL_KO[quote.product.plan]} 플랜 견적서
      </div>
      <img src={`${ASSET_BASE}/logo.svg`} alt="ZEP Logo" className={`absolute h-[24px] w-[63px] ${pdf ? 'right-[28px] top-[28px]' : 'right-[30px] top-[35px]'}`} />

      <table className={tableClass(pdf)}>
        <tbody>
          <tr>
            <th>수신</th>
            <td>{quote.recipient.organizationName}</td>
            <th rowSpan={4}>
              공
              <br />
              급
              <br />자
            </th>
            <th>등록번호</th>
            <td colSpan={3}>535-86-02323</td>
          </tr>
          <tr>
            <th>견적명</th>
            <td>
              ZEP QUIZ {PLAN_LABEL_KO[quote.product.plan]} 플랜{' '}
              {quote.product.monthCount}개월 견적
            </td>
            <th>상호명</th>
            <td>주식회사 젭</td>
            <th>대표자</th>
            <td>김상엽, 고창원</td>
          </tr>
          <tr>
            <th>견적날짜</th>
            <td>{formatDocumentDate(quote.issuedAt)}</td>
            <th>주소</th>
            <td colSpan={3}>서울시 강남구 선릉로 93길 50, 11층</td>
          </tr>
          <tr>
            <th>유효기간</th>
            <td>{formatDocumentDate(quote.validUntil)}까지</td>
            <th>전화번호</th>
            <td>070-7666-9837</td>
            <th>이메일</th>
            <td>biz@zep.us</td>
          </tr>
        </tbody>
      </table>

      <table className={tableClass(pdf)}>
        <tbody>
          <tr>
            <th>견적가</th>
            <td className="font-bold">
              {`금 ${formatKoreanAmount(quote.pricing.finalAmount)} 정(₩${formatWon(quote.pricing.finalAmount)}), VAT 포함`}
            </td>
          </tr>
        </tbody>
      </table>

      <table className={tableClass(pdf, pdf ? 'mt-[14px]' : 'mt-[20px]')} style={{
      tableLayout: 'fixed'
    }}>
        <colgroup>
          <col style={{
          width: '8%'
        }} />
          <col style={{
          width: '8%'
        }} />
          <col style={{
          width: '14%'
        }} />
          <col style={{
          width: '21%'
        }} />
          <col style={{
          width: '17%'
        }} />
          <col style={{
          width: '32%'
        }} />
        </colgroup>
        <thead>
          <tr>
            <th colSpan={6}>산출 내역</th>
          </tr>
        </thead>
        <tbody>
          <tr className="[&>td]:font-bold">
            <td>No.</td>
            <td>플랜</td>
            <td>사용 인원</td>
            <td>1인 당 단가</td>
            <td>이용 개월 수</td>
            <td>결제 금액</td>
          </tr>
          <tr>
            <td>1</td>
            <td>
              <strong>{PLAN_LABEL_EN[quote.product.plan]}</strong>
            </td>
            <td>{quote.product.userCount}</td>
            <td>
              <AmountCell amount={quote.pricing.regularUnitAmount} compact />
            </td>
            <td>{quote.product.monthCount}</td>
            <td>
              <AmountCell amount={quote.pricing.originalAmount} />
            </td>
          </tr>
          {discountRows.map((discount, index) => <tr key={`${discount.label}-${index}`}>
              <td>{index + 2}</td>
              <td colSpan={4}>{discount.label}</td>
              <td>
                <AmountCell amount={discount.amount} parenthesized />
              </td>
            </tr>)}
          {Array.from({
          length: emptyRowCount
        }).map((_, index) => <tr key={index}>
              <td> &nbsp;</td>
              <td />
              <td />
              <td />
              <td />
              <td />
            </tr>)}
          <tr>
            <td colSpan={5} className="font-bold">
              최종 견적
            </td>
            <td className="font-bold">
              <AmountCell amount={quote.pricing.finalAmount} />
            </td>
          </tr>
        </tbody>
      </table>

      <table className={tableClass(pdf, '[&_td:nth-child(1)]:whitespace-nowrap [&_td:nth-child(2)]:whitespace-nowrap [&_th:nth-child(1)]:whitespace-nowrap [&_th:nth-child(2)]:whitespace-nowrap')} style={{
      tableLayout: 'fixed'
    }}>
        <colgroup>
          <col style={{
          width: '7%'
        }} />
          <col style={{
          width: '15%'
        }} />
          <col style={{
          width: '78%'
        }} />
        </colgroup>
        <thead>
          <tr>
            <th colSpan={3}>기능 상세</th>
          </tr>
          <tr>
            <th>번 호</th>
            <th>항 목</th>
            <th>내 용</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>1</td>
            <td>사용 인원</td>
            <td>{quote.product.userCount}명</td>
          </tr>
          <tr>
            <td>2</td>
            <td>사용 시작일</td>
            <td>{formatDocumentDate(quote.product.startDate)}</td>
          </tr>
          <tr>
            <td>3</td>
            <td>만료일</td>
            <td>{formatDocumentDate(quote.product.endDate)}</td>
          </tr>
          <tr>
            <td>4</td>
            <td>{PLAN_LABEL_KO[quote.product.plan]} 플랜 기능</td>
            <td>{getPlanFeature(quote.product.plan)}</td>
          </tr>
          <tr>
            <td>5</td>
            <td>요금제 유형</td>
            <td>
              {quote.product.pricingType === 'group' ? '학교·단체' : '개인'}
            </td>
          </tr>
        </tbody>
      </table>

      <table className={tableClass(pdf, pdf ? '[&_tbody_tr]:h-[164px]' : '[&_tbody_tr]:h-[188px]')}>
        <thead>
          <tr>
            <th colSpan={2}>비고</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="!border-r-0 !text-left !align-top">{annotation}</td>
            <td className="w-[180px] min-w-[180px] !border-l-0 !pb-[24px] !pr-[24px] !text-right !align-bottom">
              &nbsp;
              <br />
              &nbsp;
              <img src={`${ASSET_BASE}/zep_stamp.png`} alt="" className="inline-block h-[131px] w-[107px] object-contain" />
              <br />
              &nbsp;
            </td>
          </tr>
        </tbody>
      </table>
    </div>;
});